/* eslint-disable max-lines */
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import { colors, layout } from '../styles';
import BankingAccountsService from '../../../../../services/BankingAccounts';
import FileInput, { FileInputHandle } from '../components/FileInput';
import { BaseStepProps } from './BaseStepProps';

interface BankDraft {
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  confirmAccountNumber: string;
  accountType: 'checking' | 'savings';
  fileName?: string;
}

const empty: BankDraft = {
  bankName: '',
  routingNumber: '',
  accountNumber: '',
  confirmAccountNumber: '',
  accountType: 'checking',
};

const BankAccountStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const initial = (draft as BankDraft) || empty;
  const [data, setData] = useState<BankDraft>({ ...empty, ...initial });
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<FileInputHandle>(null);

  const update = (patch: Partial<BankDraft>) => {
    const next = { ...data, ...patch };
    setData(next);
    saveDraft(next);
  };

  const matches = data.accountNumber && data.accountNumber === data.confirmAccountNumber;
  const canSubmit =
    !!data.bankName &&
    data.routingNumber.length === 9 &&
    data.accountNumber.length >= 4 &&
    matches &&
    !!file &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        employeeId: tokenData.employeeId,
        bankName: data.bankName,
        routingNumber: data.routingNumber,
        accountNumber: data.accountNumber,
        confirmAccountNumber: data.confirmAccountNumber,
        accountType: data.accountType,
      };
      const banking = await BankingAccountsService(token).create(body);
      if (file) {
        const formData = new FormData();
        formData.append('document', file);
        await BankingAccountsService(token).uploadFile(banking?.data?.id, formData);
      }
      clearDraft();
      fileInputRef.current?.reset();
      setFile(null);
      toast.success('Direct deposit account saved.');
      onNext();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save banking information.');
    } finally {
      setSubmitting(false);
    }
  };

  const accountOptions: { value: 'checking' | 'savings'; label: string; description: string }[] = [
    { value: 'checking', label: 'Checking Account', description: 'Most common for direct deposit' },
    { value: 'savings', label: 'Savings Account', description: 'Use only if your bank explicitly supports it' },
  ];

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={onSaveLater}
      onNext={handleSubmit}
      nextLabel={submitting ? 'Saving...' : 'Save & Continue'}
      nextDisabled={!canSubmit}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Banking Account</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        Set up direct deposit to receive your paycheck directly into your bank account.
      </p>

      <div style={layout.infoBox}>
        <i className="fas fa-file-invoice-dollar" />
        <span>
          <strong>Need your bank details?</strong>
          <br />
          You can find your routing and account numbers on a check or by logging into your online banking.
        </span>
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 700, margin: '16px 0 10px' }}>Account Type *</h4>
      {accountOptions.map((opt) => (
        <div
          key={opt.value}
          onClick={() => update({ accountType: opt.value })}
          style={{
            ...layout.optionCard,
            ...(data.accountType === opt.value ? layout.optionCardSelected : {}),
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `2px solid ${data.accountType === opt.value ? colors.accent : '#9CA3AF'}`,
                position: 'relative',
                flexShrink: 0,
              }}
            >
              {data.accountType === opt.value && (
                <div
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: colors.accent,
                  }}
                />
              )}
            </div>
            <strong>{opt.label}</strong>
          </div>
          <span style={{ fontSize: 12, color: colors.muted, marginLeft: 30 }}>{opt.description}</span>
        </div>
      ))}

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Bank Name *</label>
        <input
          type="text"
          value={data.bankName}
          onChange={(e) => update({ bankName: e.target.value })}
          placeholder="e.g., Chase Bank"
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Routing Number *</label>
        <input
          type="text"
          value={data.routingNumber}
          onChange={(e) => update({ routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) })}
          placeholder="9-digit routing number"
          maxLength={9}
          style={layout.input}
        />
        <div style={{ fontSize: 12, color: colors.muted, marginTop: 4 }}>
          The 9-digit number at the bottom left of your check
        </div>
        {data.routingNumber.length > 0 && data.routingNumber.length < 9 && (
          <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>
            Routing number must be exactly 9 digits.
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Account Number *</label>
        <input
          type="text"
          value={data.accountNumber}
          onChange={(e) => update({ accountNumber: e.target.value.replace(/\D/g, '') })}
          onPaste={(e) => e.preventDefault()}
          placeholder="Enter account number"
          style={layout.input}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Confirm Account Number *</label>
        <input
          type="text"
          value={data.confirmAccountNumber}
          onChange={(e) => update({ confirmAccountNumber: e.target.value.replace(/\D/g, '') })}
          onPaste={(e) => e.preventDefault()}
          placeholder="Re-enter account number"
          style={layout.input}
        />
        {data.confirmAccountNumber && !matches && (
          <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>Account numbers do not match</div>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={layout.label}>Upload Blank Check or Direct Deposit Form *</label>
        <FileInput
          ref={fileInputRef}
          selected={file}
          onChange={(f) => {
            setFile(f);
            update({ fileName: f?.name });
          }}
          hint="PNG, JPG or PDF"
        />
      </div>
    </StepShell>
  );
};

export default BankAccountStep;
